import React, { useEffect, useState } from "react";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { Box, Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { IntegrationProcessService } from "../../../Component/Api/IntegrationProcess/IntegrationProcessService";
import { Submissions } from "../../../Component/Model/Dto/VerificationAgency/IntegrationProcess/Submissions";
import { LongTableDateHelper, StringDateTimeHelper, TableTimeHelper } from "../../../Component/Model/helpers";
import { VaIntegrationStatus } from "../../../Component/Model/Enum/BEE123Enums";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import colors from "../../../assets/theme/base/colors";

const { dark, error } = colors;

interface IProps {
    selectedSubmissionId: number;
    showApprovalModal: boolean;
    closeApprovalModal: () => void;
}

export const SubmissionApprovalModal = (props: IProps) => {
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [scorecardName, setScorecardName] = useState<string>();
    const [selectedElements, setSelectedElements] = React.useState<string[]>([]);
    const [dateSubmitted, setDateSubmitted] = useState<string>("");
    const [clientName, setClientName] = React.useState<string>();

    const fetchData = async () => {
        try {
            if (props.selectedSubmissionId > 0) {
                let detail = await IntegrationProcessService.GetSubmissionDetails(props.selectedSubmissionId);
                if (detail.data) {
                    setScorecardName(detail.data.scorecardName);
                    setSelectedElements(detail.data.flags ?? []);
                    setDateSubmitted(StringDateTimeHelper(detail.data.createdDate.toString()));
                    setClientName(detail.data.clientName);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        fetchData();
    }, [props.selectedSubmissionId]);



    const ProcessSubmission = async (status: VaIntegrationStatus) => {
        setIsSaving(true);
        try {
            const submitDetails: Submissions.SubmissionDetailDto =
            {
                vaSubmissionProcessId: props.selectedSubmissionId,
                status: status
            }
            await IntegrationProcessService.ProcessSubmission(submitDetails);
            toast.success("Scorecard Submission processed")

        } catch (error: any) {
            console.log(error);
            toast.error(error.response.data);
        } finally {
            setIsSaving(false);
        }
    }


    return (
        <Dialog disableEnforceFocus open={props.showApprovalModal} onClose={() => { props.closeApprovalModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="customized-dialog-title" onClose={() => props.closeApprovalModal()}>
                <Grid container paddingLeft={2} paddingTop={1}>
                    <MDTypography variant='body1' fontWeight='bold' color='primary'>Action Incoming Scorecard Submission</MDTypography>
                </Grid>
            </CustomDialogTitle>
            <DialogContent sx={{ height: '450px', width: '650px' }}>
                <form id='SubmissionApprovalForm'>
                    <Grid container paddingLeft={2}>
                        <MDTypography variant='body'>The following scorecard has been submitted for verification: <strong>{scorecardName}</strong>.</MDTypography>
                    </Grid>
                    <Grid container paddingLeft={2} paddingTop={2} paddingBottom={0}>
                        <MDTypography variant='body'><strong>Data pages</strong> that were submitted:</MDTypography>
                    </Grid>
                    <Grid container paddingLeft={4} paddingBottom={2} paddingTop={0.5} style={{ color: dark.main }}>
                        {selectedElements.map((element) => (
                            <Grid item xs={6} key={element} paddingTop={1}>
                                <li {...props} key={element} value={element}>
                                    <MDTypography variant='body'>{element}</MDTypography>
                                </li>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container paddingLeft={2} paddingTop={2} paddingBottom={2}>
                        <MDTypography variant='body'><strong>Supporting documents:</strong></MDTypography>
                        <MDTypography variant='body'>All supporting documents uploaded to this scorecard at the time of submission are included and will be available after you've accepted the submission.</MDTypography>
                    </Grid>
                    <Grid container paddingLeft={2} paddingTop={1}>
                        <MDTypography variant='body'><strong>Submission details:</strong></MDTypography>
                        <MDTypography variant='body'>This scorecard was submitted by {clientName} on {LongTableDateHelper(dateSubmitted.toString())} at {TableTimeHelper(dateSubmitted.toString())}.</MDTypography>
                    </Grid>

                </form>
            </DialogContent>

            <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '8px 16px' }}>
                    <LoadingButton
                        loading={isSaving}
                        form='AssignForm'
                        type="submit"
                        color='primary'
                        disabled={isSaving}
                        sx={{
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: error.main,
                            color: error.main,
                            '&:hover': {
                                borderColor: 'error.main',
                                color: 'error.main',
                            },
                        }}
                        onClick={() => {
                            ProcessSubmission(VaIntegrationStatus.Rejected)
                                .then(() => {
                                    props.closeApprovalModal();
                                })
                                .catch(() => {
                                    toast.error("An error occured while processing the submission")
                                });
                        }}>
                        Reject
                    </LoadingButton>
                    <LoadingButton
                        loading={isSaving}
                        variant='outlined'
                        form='AssignForm'
                        type="submit"
                        color='primary'
                        disabled={isSaving}
                        onClick={() => {
                            ProcessSubmission(VaIntegrationStatus.Accepted)
                                .then(() => {
                                    props.closeApprovalModal();
                                })
                                .catch(() => {
                                    toast.error("An error occured while processing the submission")
                                });
                        }}>
                        Accept
                    </LoadingButton>
                </Box>
            </DialogActions>
        </Dialog>
    )
}